import React, { useState, useEffect } from "react";
import { Modal, Box, Button, Typography, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { toast } from "react-toastify";
const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const CouponModal = ({ open, onClose, onApplyCoupon, appliedCoupon, totalPrice }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchCoupons();
    }
  }, [open]);

  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/payment/coupons/`);
      if (!response.ok) throw new Error("Failed to fetch coupons");
      const data = await response.json();
      setCoupons(data);
    } catch (err) {
      setError(err.message);
      toast.error(err.message, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
    setLoading(false);
  };

  const handleApply = (coupon) => {
    // Check if coupon is already applied
    if (appliedCoupon && appliedCoupon.id === coupon.id) {
      toast.info("This coupon is already applied.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    // Validate min_amount_to_apply
    const minAmount = parseFloat(coupon.min_amount_to_apply);
    if (totalPrice < minAmount) {
      toast.error(`Cart total must be at least ₹${minAmount.toFixed(2)} to apply this coupon.`, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    // Apply the coupon
    onApplyCoupon(coupon, appliedCoupon);
    toast.success(`Coupon ${coupon.code} applied successfully!`, {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ width: 400, bgcolor: "white", p: 3, mx: "auto", mt: 10, borderRadius: 2, textAlign: "center" }}>
        <Typography variant="h6">Select a Coupon</Typography>
        {loading && <Typography>Loading coupons...</Typography>}
        {error && <Typography color="error">{error}</Typography>}
        <List>
          {coupons.map((coupon) => {
            const isApplied = appliedCoupon && appliedCoupon.id === coupon.id;
            const isEligible = totalPrice >= parseFloat(coupon.min_amount_to_apply);
            return (
              <ListItem key={coupon.id} disablePadding>
                <ListItemButton
                  onClick={() => handleApply(coupon)}
                  disabled={isApplied || !isEligible}
                  sx={{
                    backgroundColor: isApplied ? "#e0e0e0" : isEligible ? "inherit" : "#f5f5f5",
                    cursor: isApplied || !isEligible ? "not-allowed" : "pointer",
                  }}
                >
                  <ListItemText
                    primary={`${coupon.code} - ₹${parseFloat(coupon.discount).toFixed(2)} Off`}
                    secondary={
                      isApplied
                        ? "Already applied"
                        : !isEligible
                        ? `Requires min. ₹${parseFloat(coupon.min_amount_to_apply).toFixed(2)}`
                        : coupon.description
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Button onClick={onClose} variant="contained" color="secondary" sx={{ mt: 2 }}>
          Close
        </Button>
      </Box>
    </Modal>
  );
};

export default CouponModal;